import { CheckCircle2, Clock3, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import client from '../api/client';
import { formatDate, formatRupiah } from '../utils/format';

const actions = {
  DRAFT: ['CONFIRMED', 'CANCELLED'],
  CONFIRMED: ['COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const location = useLocation();

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await client.get('/orders');

      setOrders(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Pesanan gagal dimuat. Pastikan backend UniLoot berjalan.'
      );

      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const changeStatus = async (orderId, status) => {
    try {
      setError('');

      await client.put(`/orders/${orderId}/status`, {
        status,
      });

      await loadOrders();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Status pesanan gagal diubah.'
      );
    }
  };

  const getButtonText = (status) => {
    if (status === 'CONFIRMED') {
      return 'Konfirmasi';
    }

    if (status === 'COMPLETED') {
      return 'Selesaikan';
    }

    if (status === 'CANCELLED') {
      return 'Batalkan';
    }

    return status;
  };

  return (
    <main className="page section">
      <div className="page-heading">
        <div>
          <span className="eyebrow">
            Riwayat transaksi
          </span>

          <h1>Pesanan Saya</h1>
        </div>

        <button
          type="button"
          className="btn secondary compact"
          onClick={loadOrders}
          disabled={loading}
        >
          {loading ? 'Memuat...' : 'Muat Ulang'}
        </button>
      </div>

      {location.state?.success && (
        <div className="alert success">
          {location.state.success}
        </div>
      )}

      {error && (
        <div className="alert error">
          {error}
        </div>
      )}

      {loading && (
        <div className="state-box">
          Memuat data pesanan...
        </div>
      )}

      {!loading && orders.length === 0 && (
        <div className="state-box">
          Belum ada pesanan.
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="orders-list">
          {orders.map((order) => {
            const availableActions =
              actions[order.status] || [];

            return (
              <article
                className="order-card"
                key={order._id}
              >
                <div className="order-head">
                  <div>
                    <span>
                      {order.orderNumber}
                    </span>

                    <h2>
                      {formatDate(order.createdAt)}
                    </h2>
                  </div>

                  <span
                    className={`status ${String(
                      order.status
                    ).toLowerCase()}`}
                  >
                    {order.status === 'COMPLETED' ? (
                      <CheckCircle2 />
                    ) : order.status ===
                      'CANCELLED' ? (
                      <XCircle />
                    ) : (
                      <Clock3 />
                    )}

                    {order.status}
                  </span>
                </div>

                <div className="order-items">
                  {(order.items || []).map((item) => (
                    <div
                      key={
                        item.productId ||
                        `${order._id}-${item.name}`
                      }
                    >
                      <img
                        src={
                          item.imageUrl ||
                          '/images/placeholder-product.svg'
                        }
                        alt={item.name}
                        onError={(event) => {
                          event.currentTarget.src =
                            '/images/placeholder-product.svg';
                        }}
                      />

                      <span>
                        {item.name} × {item.quantity}
                      </span>

                      <b>
                        {formatRupiah(
                          Number(item.price) *
                            Number(item.quantity)
                        )}
                      </b>
                    </div>
                  ))}
                </div>

                <div className="order-foot">
                  <strong>
                    Total{' '}
                    {formatRupiah(order.totalPrice)}
                  </strong>

                  <div>
                    {availableActions.map(
                      (status) => (
                        <button
                          type="button"
                          key={status}
                          className={`btn ${
                            status === 'CANCELLED'
                              ? 'danger'
                              : 'primary'
                          } compact`}
                          onClick={() =>
                            changeStatus(
                              order._id,
                              status
                            )
                          }
                        >
                          {getButtonText(status)}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
}